/** @typedef {import('headlight-configurable-application').Pict} Pict */

const MaterialsEntityInputType = require('./PictProvider-InputType-MaterialsEntitySelector.js');
const ScopedEntityData = require('./_ScopedEntityData.js');
const libLabRouting = require('./_LabRouting.js');

/**
 * Install the pin-to-top resolver for a lab selector's select2 options, when the descriptor scopes by
 * `LabScope` and the LabScopeResolver is registered. The resolver answers with its preference ranking
 * (`LabScopeResolver.getPreferredLabIDs`); nothing is hidden.
 *
 * @param {import('./PictProvider-Select2Util.js').Select2AjaxOptions} pConfigOptions
 * @param {Object} pInput @param {number} [pRowIndex] @param {Object} [pView]
 */
function applyLabPriorityIDs(pPict, pConfigOptions, pInput, pRowIndex, pView)
{
	const tmpEngine = pPict.providers.EntityFilterEngine;
	const tmpResolver = pPict.providers.LabScopeResolver;
	if (!tmpEngine || !tmpResolver || pConfigOptions.GetPriorityIDs)
	{
		return;
	}
	const tmpFilters = (pInput && pInput.PictForm) ? pInput.PictForm.DynamicFilters : null;
	if (!Array.isArray(tmpFilters))
	{
		return;
	}
	const tmpFilter = tmpFilters.find((pEntry) => pEntry && pEntry.LabScope);
	if (!tmpFilter)
	{
		return;
	}
	const tmpRowIndex = (pRowIndex === undefined) ? null : pRowIndex;
	pConfigOptions.GetPriorityIDs = () =>
	{
		return tmpResolver.getPreferredLabIDs(tmpFilter.LabScope,
			{
				IDTest: tmpEngine._readLabScopeTestID(tmpFilter, pInput, tmpRowIndex, pView || null),
			});
	};
}

class MaterialsLabSelectorInputType extends MaterialsEntityInputType
{
	constructor(pFable, pOptions, pServiceHash)
	{
		if (!pOptions)
		{
			pOptions = { };
		}
		pOptions.Entity = 'Lab'; //TODO: use default options for this?
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict & { TransactionTracking: any }} */
		this.pict;
	}

	/**
	 * Initializes the input element for the Pict provider select input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The input value.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @return {boolean} - Returns true if the input element is successfully initialized, false otherwise.
	 */
	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		if (!pInput.PictForm)
		{
			pInput.PictForm = { };
		}
		if (pInput.PictForm.Multiple == null)
		{
			pInput.PictForm.Multiple = true;
		}
		if (pInput.PictForm.EntityTag == null)
		{
			pInput.PictForm.EntityTag = 'Name';
		}
		if (pInput.PictForm.Multiple)
		{
			// update the parent ".control.select" to have ".is-multiple" for proper styling
			const tmpSelectWrapper = document.querySelector(`${pHTMLSelector} ~ .field .control.select`);
			if (tmpSelectWrapper && !tmpSelectWrapper.classList.contains('is-multiple'))
			{
				tmpSelectWrapper.classList.add('is-multiple');
			}
		}
		const tmpResult = super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
		// sample is in charge of the family for tracking
		if (pInput.PictForm.SampleTrackingGUID && pInput.PictForm.SampleTrackingGUID != true)
		{
			this._setTrackingGUID(pInput, null, pInput.PictForm.SampleTrackingGUID);
			return tmpResult;
		}
		return tmpResult;
	}

	/**
	 * Initializes a tabular input element.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The input value.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The index of the row.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @return {any} - The result of the initialization.
	 */
	onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		if (!pInput.PictForm)
		{
			pInput.PictForm = { };
		}
		if (pInput.PictForm.Multiple == null)
		{
			pInput.PictForm.Multiple = true;
		}
		if (pInput.PictForm.EntityTag == null)
		{
			pInput.PictForm.EntityTag = 'Name';
		}
		if (pInput.PictForm.Multiple)
		{
			// update the parent ".control.select" to have ".is-multiple" for proper styling
			const tmpSelectWrapper = document.querySelector(`${pHTMLSelector} ~ .field .control.select`);
			if (tmpSelectWrapper && !tmpSelectWrapper.classList.contains('is-multiple'))
			{
				tmpSelectWrapper.classList.add('is-multiple');
			}
		}
		const tmpResult = super.onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
		if (pInput.PictForm.SampleTrackingGUID && pInput.PictForm.SampleTrackingGUID != true)
		{
			this._setTrackingGUID(pInput, pRowIndex, pInput.PictForm.SampleTrackingGUID);
			return tmpResult;
		}
		return tmpResult;
	}

	/**
	 * @param {Object} pView
	 * @param {Object} pInput
	 * @param {any} pValue
	 * @param {string} pHTMLSelector
	 * @param {number|null} pRowIndex
	 * @param {string} pEvent
	 * @param {string} pTransactionGUID
	 */
	_handleEvent(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID)
	{
		if (!pEvent || typeof pEvent !== 'string')
		{
			return;
		}

		const [ tmpEventType, tmpTrackingGUID, tmpEventPayload ] = pEvent.split(':');

		if (tmpEventType === 'SampleChanged')
		{
			if (tmpTrackingGUID !== this._getTrackingGUID(pInput, pRowIndex))
			{
				return;
			}
			if (!this._shouldRefreshSampleJoins(pInput))
			{
				return;
			}
			const tmpLabAddress = this._getRecordAddress(pView, pInput, pRowIndex);
			const tmpSampleLabJoinAddress = pInput.PictForm.SampleLabJoinAddress || `${tmpLabAddress}_SampleJoins`;
			const tmpSampleRecord = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpEventPayload);
			const tmpRefreshEvent = 'SampleLabJoinRefresh';
			if (!tmpSampleRecord || typeof tmpSampleRecord !== 'object' || Array.isArray(tmpSampleRecord))
			{
				this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpSampleLabJoinAddress, []);
				return;
			}
			if (!tmpSampleRecord.IDSample)
			{
				return;
			}
			pView.registerEventTransactionAsyncOperation(pTransactionGUID, tmpRefreshEvent);
			this.pict.HeadlightRestClient.getJSON(`SampleLabJoins/FilteredTo/FBV~IDSample~EQ~${tmpSampleRecord.IDSample}`, (pError, pRecords) =>
			{
				// A failed fetch is not evidence of "no labs". Treating it as an empty set and writing that
				// through overwrites the persisted selection on a transient network error -- and the document
				// auto-save then persists the loss with no user action. Abort instead: leave both the joins
				// address and the input value exactly as loaded.
				if (pError || !Array.isArray(pRecords))
				{
					this.log.error(`MaterialsLabSelectorInputType: failed to load SampleLabJoins for Sample ID ${tmpSampleRecord.IDSample} -- keeping the persisted value.`, { pError, pRecords });
					pView.eventTransactionAsyncOperationComplete(pTransactionGUID, tmpRefreshEvent);
					return;
				}
				const tmpLabValue = pRecords.map((pJoin) => pJoin.IDLab).join(',');
				// Fail-open backstop, same instinct as the entity selector's autofill-scope vet: an empty result
				// cannot be distinguished from "the joins could not be resolved", so it must not clobber a value
				// the document already carries. Only an explicit user action may empty this. A non-empty result
				// is authoritative and still corrects a stale selection.
				const tmpExistingValue = this.pict.manifest.getValueAtAddress(
					pView.getMarshalDestinationObject(), this._getRecordAddress(pView, pInput, pRowIndex, ''));
				if (tmpLabValue === '' && String(tmpExistingValue ?? '').trim() !== '')
				{
					this._dropSoftDeletedHeldJoins(pView, pInput, pRowIndex,
						{ Entity: 'SampleLabJoin', ValueField: 'IDLab', JoinAddress: tmpSampleLabJoinAddress, Value: tmpExistingValue, Label: 'MaterialsLabSelectorInputType' },
						(pDropped) =>
						{
							if (pDropped < 1)
							{
								this.log.warn(`MaterialsLabSelectorInputType: Sample ID ${tmpSampleRecord.IDSample} resolved no SampleLabJoins but the document carries [${tmpExistingValue}] -- keeping the persisted value.`);
							}
							pView.eventTransactionAsyncOperationComplete(pTransactionGUID, tmpRefreshEvent);
						});
					return;
				}
				this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpSampleLabJoinAddress, pRecords);
				// Re-binding to a different Sample replaces this input's joins wholesale. It routes through
				// setDataByInput, which enters onDataChange looking exactly like a user de-selection -- but the
				// outgoing sample's joins stay attached to that sample and are not this form's to delete.
				ScopedEntityData.beginProgrammaticLoad(this.pict);
				try
				{
					if (pRowIndex != null)
					{
						pView.setDataTabularByHash(pInput.PictForm.GroupIndex, pInput.Hash, pRowIndex, tmpLabValue);
					}
					else
					{
						pView.setDataByInput(pInput, tmpLabValue);
					}
				}
				finally
				{
					ScopedEntityData.endProgrammaticLoad(this.pict);
				}
				pView.eventTransactionAsyncOperationComplete(pTransactionGUID, tmpRefreshEvent);
			});
			return;
		}

		if (tmpEventType === 'ProjectEntityDataRecords')
		{
			const tmpTarget = this._getProjectionTarget(pInput, pEvent);
			if (!tmpTarget)
			{
				return;
			}
			this._syncRecordsToEntityData(pView, pInput, pRowIndex, pTransactionGUID, tmpTarget);
			const tmpLabAddress = this._getRecordAddress(pView, pInput, pRowIndex);
			const tmpDocumentPolyJoinAddress = pInput.PictForm.DocumentPolyJoinAddress || `${tmpLabAddress}_DocumentPolyJoin`;
			const tmpDocumentPolyJoin = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpDocumentPolyJoinAddress);
			if (tmpDocumentPolyJoin && typeof tmpDocumentPolyJoin === 'object' && !Array.isArray(tmpDocumentPolyJoin))
			{
				if (!tmpTarget.DocumentPolyJoin || typeof tmpTarget.DocumentPolyJoin !== 'object' || Array.isArray(tmpTarget.DocumentPolyJoin))
				{
					tmpTarget.DocumentPolyJoin = { };
				}
				tmpTarget.DocumentPolyJoin[tmpDocumentPolyJoin.GUIDDocumentPolyJoin] = tmpDocumentPolyJoin;
			}
		}

		if (tmpEventType === libLabRouting.SAMPLE_LAB_SYNC_EVENT)
		{
			// The per-test labs changed. Reconcile OUR value (the source of truth) and commit through setDataByInput,
			// which fires onDataChange -> _onRecordRefresh, so the join array derives itself.
			const tmpNextValue = libLabRouting.syncSampleLabSelection(this.pict, pInput, pView.getMarshalDestinationObject(),
				this._getRecordAddress(pView, pInput, null, '__Promoted'), pView.getValueByHash(pInput.Hash));
			if (tmpNextValue !== null)
			{
				pView.setDataByInput(pInput, tmpNextValue);
			}
		}
	}

	/**
	 * Handles events for the Pict-Provider-InputExtension.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value from AppData.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pEvent - The event hash that is expected to be triggered.
	 * @param {string} pTransactionGUID - The transaction GUID, if any.
	 * @returns {boolean} - Returns true.
	 */
	onAfterEventCompletion(pView, pInput, pValue, pHTMLSelector, pEvent, pTransactionGUID)
	{
		this._handleEvent(pView, pInput, pValue, pHTMLSelector, null, pEvent, pTransactionGUID);
		return super.onAfterEventCompletion(pView, pInput, pValue, pHTMLSelector, pEvent, pTransactionGUID);
	}

	/**
	 * Handles events for the Pict-Provider-InputExtension.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value from AppData.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The row index of the tabular data.
	 * @param {string} pEvent - The event hash that is expected to be triggered.
	 * @param {string} pTransactionGUID - The transaction GUID, if any.
	 * @returns {boolean} - Returns true.
	 */
	onAfterEventTabularCompletion(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID)
	{
		this._handleEvent(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID);
		return super.onAfterEventTabularCompletion(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID);
	}

	/**
	 * A de-selected Lab takes its `SampleLabJoin` with it. The joins are keyed by `IDLab`, which is
	 * also what this input's value list holds, so the retained set filters them directly.
	 * @param {Object} pView @param {Object} pInput @param {number|string|null} pRowIndex
	 * @param {Set<string>} pRetainedIdentities
	 * @return {Array<{ Entity: string, Record: Record<string, any> }>}
	 */
	_collectRemovedDependentRecords(pView, pInput, pRowIndex, pRetainedIdentities)
	{
		const tmpLabAddress = this._getRecordAddress(pView, pInput, pRowIndex);
		const tmpSampleLabJoinsAddress = pInput.PictForm.SampleLabJoinAddress || `${tmpLabAddress}_SampleJoins`;
		const tmpSampleLabJoins = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpSampleLabJoinsAddress);
		if (!Array.isArray(tmpSampleLabJoins))
		{
			return [ ];
		}
		const tmpRemovals = [ ];
		for (const tmpJoin of tmpSampleLabJoins)
		{
			if (tmpJoin && typeof tmpJoin === 'object' && !pRetainedIdentities.has(String(tmpJoin.IDLab)))
			{
				tmpRemovals.push({ Entity: 'SampleLabJoin', Record: tmpJoin });
			}
		}
		return tmpRemovals;
	}

	/**
	 * @param {Array<Record<string, any>>} pLabs
	 * @param {Array<Record<string, any>>} pCurrentSampleLabJoins
	 * @param {Record<string, any>} pSample
	 * @param {Object} pView
	 * @param {Object} pInput
	 * @param {number|string|null} pRowIndex
	 *
	 * @return {Array<Record<string, any>>}
	 */
	_syncSampleLabJoins(pLabs, pCurrentSampleLabJoins, pSample, pView, pInput, pRowIndex)
	{
		const tmpUnclaimedJoins = Array.isArray(pCurrentSampleLabJoins) ? pCurrentSampleLabJoins.slice() : [ ];
		const tmpSampleLabJoins = [ ];
		for (const tmpLab of pLabs || [])
		{
			const tmpExistingJoinIndex = tmpUnclaimedJoins.findIndex((pJoin) => pJoin.IDLab == tmpLab.IDLab);
			if (tmpExistingJoinIndex >= 0)
			{
				tmpSampleLabJoins.push(tmpUnclaimedJoins[tmpExistingJoinIndex]);
				tmpUnclaimedJoins.splice(tmpExistingJoinIndex, 1);
				continue;
			}
			const tmpNewJoin = JSON.parse(JSON.stringify(pInput.PictForm.DefaultSampleLabJoinRecord ||
				{
					GUIDSampleLabJoin: `{~D:AppData.DocumentData.ReportData.GUIDDocument~}-SLJ-${this._getSalt(pView, pInput, pRowIndex)}`,
					IDLab: tmpLab.IDLab,
					Received: 0,
				}));
			tmpNewJoin.GUIDSampleLabJoin += `-${tmpLab.IDLab}`;
			if (pSample && pSample.GUIDSample)
			{
				if (pSample.IDSample > 0)
				{
					tmpNewJoin.IDSample = pSample.IDSample;
				}
				else
				{
					tmpNewJoin.GUIDSample = pSample.GUIDSample;
				}
			}
			this._translateRecordTemplateFields(tmpNewJoin);

			if (!tmpNewJoin.GUIDSampleLabJoin)
			{
				tmpNewJoin.GUIDSampleLabJoin = this.pict.getUUID();
			}
			tmpSampleLabJoins.push(tmpNewJoin);
		}
		return tmpSampleLabJoins;
	}

	/**
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {Object} pRecord - The record that was refreshed.
	 * @param {string} pRecordAddress - The address where the record was stored.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @param {number} [pRowIndex] - The row index. If present, this is a tabular input.
	 */
	_onRecordRefresh(pView, pInput, pRecord, pRecordAddress, pTransactionGUID, pRowIndex)
	{
		const tmpLabAddress = this._getRecordAddress(pView, pInput, pRowIndex);
		if (!pRecord)
		{
			pRecord = pInput.PictForm.Multiple ? [] : { };
		}
		this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpLabAddress, pRecord);
		const tmpSampleLabJoinsAddress = pInput.PictForm.SampleLabJoinAddress || `${tmpLabAddress}_SampleJoins`;
		let tmpSampleLabJoins = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpSampleLabJoinsAddress);
		const tmpSample = this._getBoundSampleRecord(pView, pInput);
		if (!Array.isArray(tmpSampleLabJoins))
		{
			if (tmpSampleLabJoins)
			{
				this.log.error(`MaterialsLabSelectorInputType: SampleLabJoins at address ${tmpSampleLabJoinsAddress} is not an array`, { tmpSampleLabJoins });
			}
			tmpSampleLabJoins = [ ];
		}
		tmpSampleLabJoins = this._syncSampleLabJoins(Array.isArray(pRecord) ? pRecord : [ pRecord ], tmpSampleLabJoins, tmpSample, pView, pInput, pRowIndex);
		this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpSampleLabJoinsAddress, tmpSampleLabJoins);
		pView.marshalToView();
		super._onRecordRefresh(pView, pInput, pRecord, pRecordAddress, pTransactionGUID, pRowIndex);
	}

	/**
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {number|string|null} pRowIndex - The row index of the tabular data.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @param {Object} pTargetObject - The `<Entity>.<GUID>` object to project into.
	 */
	_syncRecordsToEntityData(pView, pInput, pRowIndex, pTransactionGUID, pTargetObject)
	{
		const tmpLabAddress = this._getRecordAddress(pView, pInput, pRowIndex);
		const tmpEntityData = pTargetObject;
		if (!tmpEntityData || typeof tmpEntityData !== 'object' || Array.isArray(tmpEntityData))
		{
			return;
		}

		const tmpSampleLabJoinsAddress = pInput.PictForm.SampleLabJoinAddress || `${tmpLabAddress}_SampleJoins`;
		const tmpSampleLabJoins = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpSampleLabJoinsAddress);
		if (Array.isArray(tmpSampleLabJoins))
		{
			if (!tmpEntityData.SampleLabJoin || typeof tmpEntityData.SampleLabJoin !== 'object' || Array.isArray(tmpEntityData.SampleLabJoin))
			{
				tmpEntityData.SampleLabJoin = { };
			}
			for (const tmpSampleLabJoin of tmpSampleLabJoins)
			{
				tmpEntityData.SampleLabJoin[tmpSampleLabJoin.GUIDSampleLabJoin] = tmpSampleLabJoin;
			}
		}
	}

	/**
	 * @param {import('./PictProvider-Select2Util.js').Select2AjaxOptions} pConfigOptions
	 * @param {Object} pView
	 * @param {Object} pInput
	 * @param {number} [pRowIndex]
	 */
	_updateSelect2Options(pConfigOptions, pView, pInput, pRowIndex)
	{
		super._updateSelect2Options(pConfigOptions, pView, pInput, pRowIndex);
		pConfigOptions.SearchFields = pConfigOptions.SearchFields || [ 'Name' ];
		//TODO: wrap existing HandleSearchFilters if it exists?
		pConfigOptions.HandleSearchFilters = (pFilters) =>
		{
			// Labs are customer-scoped (row-level auth), not project-scoped — just sort by Name.
			pFilters.push(`FSF~Name~ASC~0`);
			return pFilters;
		};
		applyLabPriorityIDs(this.pict, pConfigOptions, pInput, pRowIndex, pView);
	}

	/**
	 * @param {Object} pView
	 * @param {Object} pInput
	 *
	 * @return {Record<string, any>|null}
	 */
	_getBoundSampleRecord(pView, pInput)
	{
		const tmpPayloadUUID = this.pict.getUUID();
		this.pict.views.PictFormMetacontroller.triggerGlobalInputEvent(`PushRecord:${this._getTrackingGUID(pInput)}:TempData["${tmpPayloadUUID}"]`);

		let tmpSample = this.pict.TempData[tmpPayloadUUID];
		delete this.pict.TempData[tmpPayloadUUID];

		if (!tmpSample || typeof tmpSample !== 'object' || Array.isArray(tmpSample))
		{
			//FIXME: what we actually need is a way to find this via the tracking GUID
			const tmpSampleAddress = pInput.PictForm.SampleAddress || 'SampleID__Record';
			tmpSample = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpSampleAddress);
		}
		return tmpSample;
	}
}

module.exports = MaterialsLabSelectorInputType;
module.exports.default_configuration = { };
